/**
 * TractionBubble — entry point of "What traction do you want to generate?"
 *
 * Centered prompt bubble mirroring the Build UX. Single textarea, a "Link ▾"
 * dropdown to attach an existing build/repo (auto-skins the palette), and
 * 3 example "traction" preset cards that pre-fill + immediately submit.
 *
 * The submit flow promotes the linked project to a Monetize venture
 * (POST /api/monetize/projects) and forwards the resulting venture id to
 * the parent so it can render the active cockpit. For a "free-form" prompt
 * (no build linked), it still creates a lightweight venture record with the
 * description as the seed name + first instruction.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUp, Link2, ChevronDown, X, Search, Paperclip,
  Sparkles, Box, Package, Loader2,
} from 'lucide-react';
import { paletteForVenture, DEFAULT_PALETTE } from './brandInheritance';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EXAMPLES = [
  {
    id: 'hr-smb',
    title: 'HR-tech SMB',
    subtitle: 'AI onboarding · B2B SaaS · €49/mo',
    prompt: 'AI HR onboarding tool for European SMBs (20-200 employees). B2B SaaS at €49/month. Help me visualize my traction: who buys, who I should reach on LinkedIn, what competitors I face, and the SEO gaps I can own.',
    seed: 'HR-tech SMB venture',
  },
  {
    id: 'creator-tools',
    title: 'Creator tools',
    subtitle: 'TikTok creators · Freemium · Viral',
    prompt: 'Video editing AI for TikTok creators (micro to mid). Freemium with paid tier at $19/mo. Show me my traction system: which creators to partner with, viral content angles, competitor positioning, and the product-placement opportunities.',
    seed: 'Creator tools venture',
  },
  {
    id: 'cafes-paris',
    title: 'Cafés Paris',
    subtitle: 'POS + loyalty · Local · Outbound',
    prompt: 'POS + loyalty app for independent Paris cafés (100-500 venues). One-time setup + €29/mo. Map my traction: which cafés to call first, local influencers, competitor coverage, and ground-game outreach plan.',
    seed: 'Cafés Paris venture',
  },
];

export default function TractionBubble({ onLaunch }) {
  const [text, setText] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linked, setLinked] = useState(null);   // { project_id, title, kind }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  // Auto-skin the bubble from the linked project — instant wow preview
  const palette = useMemo(() => {
    if (linked) {
      return paletteForVenture({
        name: linked.title,
        project_id: linked.project_id,
        project: { title: linked.title, subtitle: linked.subtitle },
      });
    }
    return DEFAULT_PALETTE;
  }, [linked]);

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.focus();
  }, []);

  const submit = async (overridePrompt = null, overrideSeed = null) => {
    const promptText = (overridePrompt ?? text).trim();
    if (!promptText && !linked) {
      setError("Describe your idea or link a build to start.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        // If a build is linked we promote it; otherwise the backend will
        // create a venture-only record with the seed name + initial brief.
        project_id: linked?.project_id || null,
        name: linked?.title || overrideSeed || promptText.slice(0, 80) || 'New venture',
        initial_brief: promptText,
      };
      const r = await fetch(`${API}/api/monetize/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const mp = await r.json();
      onLaunch?.(mp, { palette, prompt: promptText, linked });
    } catch (e) {
      setError(e.message || 'Failed to start');
      setSubmitting(false);
    }
  };

  const submitExample = (ex) => {
    setText(ex.prompt);
    setTimeout(() => submit(ex.prompt, ex.seed), 80);  // small delay so user sees the text appear
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      data-testid="traction-bubble"
      data-palette={palette.key}
      style={{
        flex: 1, height: '100%', overflowY: 'auto',
        background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
        color: '#0F0F1A',
        position: 'relative',
      }}
    >
      {/* Soft ambient gradient */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(70% 60% at 50% 18%, ${palette.accent}1A, transparent 60%),
                     radial-gradient(50% 50% at 50% 8%, ${palette.accent2}10, transparent 70%)`,
        transition: 'background 0.6s ease',
      }} />

      <div style={{
        position: 'relative', maxWidth: 920, margin: '0 auto',
        padding: '90px 28px 60px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        {/* Eyebrow */}
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'rgba(15,15,30,0.45)', display: 'inline-flex', alignItems: 'center', gap: 6,
          marginBottom: 14,
        }}>
          <Sparkles size={11} /> Traction Build · Monetize
        </span>

        {/* Hero title */}
        <h1 style={{
          fontSize: 'clamp(28px, 4.4vw, 46px)', fontWeight: 800,
          letterSpacing: '-0.025em', lineHeight: 1.05,
          textAlign: 'center', margin: 0, color: '#0F0F1A',
        }}>
          What traction do you want to generate?
        </h1>
        <p style={{
          fontSize: 13.5, color: 'rgba(15,15,30,0.6)',
          textAlign: 'center', maxWidth: 560,
          margin: '14px 0 30px', lineHeight: 1.55,
        }}>
          Describe your product. Link a build if you have one. The preview surfaces
          your competitors, your ICP, your influencers and your paths to the first dollar.
        </p>

        {/* Bubble */}
        <div style={{
          width: '100%', maxWidth: 780,
          background: '#FFFFFF',
          border: '1px solid rgba(15,15,30,0.08)',
          borderRadius: 18,
          boxShadow: '0 24px 48px -16px rgba(15,15,30,0.18), 0 2px 6px rgba(15,15,30,0.04)',
          overflow: 'visible', position: 'relative',
        }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Describe your idea, your ICP, your positioning…"
            data-testid="traction-bubble-input"
            disabled={submitting}
            rows={4}
            style={{
              width: '100%', boxSizing: 'border-box',
              border: 'none', outline: 'none', resize: 'none',
              padding: '20px 22px 8px',
              background: 'transparent',
              fontFamily: 'inherit', fontSize: 14.5, lineHeight: 1.55,
              color: '#0F0F1A',
              minHeight: 90,
            }}
          />

          {/* Toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px 12px',
            flexWrap: 'wrap',
          }}>
            <ToolbarButton title="Attach a file (soon)" disabled>
              <Paperclip size={13} />
            </ToolbarButton>

            <LinkPicker
              open={linkOpen}
              setOpen={setLinkOpen}
              linked={linked}
              setLinked={setLinked}
              palette={palette}
            />

            <ModelChip label="E-1" />
            <ModelChip label="Claude Sonnet 4.5" />
            <ModelChip label="Aria" accent={palette.accent} />

            <div style={{ flex: 1 }} />

            <button
              onClick={() => submit()}
              disabled={submitting || (!text.trim() && !linked)}
              data-testid="traction-bubble-submit"
              style={{
                width: 42, height: 42, borderRadius: 999,
                background: submitting || (!text.trim() && !linked) ? 'rgba(15,15,30,0.08)' : '#0F0F1A',
                color: submitting || (!text.trim() && !linked) ? 'rgba(15,15,30,0.4)' : '#FFFFFF',
                border: 'none', cursor: submitting ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.18s', fontFamily: 'inherit',
                boxShadow: submitting || (!text.trim() && !linked) ? 'none' : `0 6px 18px ${palette.accent}55`,
              }}
            >
              {submitting ? <Loader2 size={16} style={{ animation: 'spin 0.7s linear infinite' }} /> : <ArrowUp size={16} strokeWidth={2.5} />}
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: 14, fontSize: 12, color: '#FF6B6B',
            padding: '8px 14px',
            background: 'rgba(255,107,107,0.08)',
            border: '1px solid rgba(255,107,107,0.22)',
            borderRadius: 8,
          }}>
            {error}
          </div>
        )}

        {/* Helper */}
        <div style={{
          fontSize: 11, color: 'rgba(15,15,30,0.4)',
          marginTop: 14, letterSpacing: '0.02em',
        }}>
          Tip&nbsp;: <kbd style={kbdStyle}>⌘</kbd> + <kbd style={kbdStyle}>↵</kbd> to launch
        </div>

        {/* Examples */}
        <div style={{
          marginTop: 38, width: '100%', maxWidth: 780,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12,
        }}>
          {EXAMPLES.map((ex, i) => (
            <ExampleCard
              key={ex.id}
              ex={ex}
              disabled={submitting}
              palette={paletteForVenture({ name: ex.title })}
              onClick={() => submitExample(ex)}
              delayMs={i * 80}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ToolbarButton({ children, onClick, active, disabled, title, accent }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        height: 34, padding: '0 12px',
        background: active ? (accent ? `${accent}22` : 'rgba(15,15,30,0.05)') : 'transparent',
        border: `1px solid ${active && accent ? `${accent}55` : 'rgba(15,15,30,0.08)'}`,
        color: active && accent ? accent : 'rgba(15,15,30,0.65)',
        borderRadius: 999,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600,
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function ModelChip({ label, accent }) {
  return (
    <span style={{
      height: 28, padding: '0 10px',
      background: 'rgba(15,15,30,0.03)',
      border: '1px solid rgba(15,15,30,0.06)',
      color: accent || 'rgba(15,15,30,0.55)',
      borderRadius: 999,
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: 'inherit', fontSize: 10.5, fontWeight: 600,
      letterSpacing: '0.02em',
    }}>
      {accent && <span style={{ width: 6, height: 6, borderRadius: 999, background: accent, boxShadow: `0 0 8px ${accent}` }} />}
      {label}
    </span>
  );
}

function LinkPicker({ open, setOpen, linked, setLinked, palette }) {
  // If something is linked, show the chip with × to unlink
  if (linked) {
    return (
      <button
        type="button"
        onClick={() => setLinked(null)}
        data-testid="traction-link-chip"
        title="Unlink"
        style={{
          height: 34, padding: '0 12px 0 10px',
          background: `${palette.accent}1F`,
          border: `1px solid ${palette.accent}55`,
          color: palette.accent === '#5EEAD4' ? '#0EA5A4' : palette.accent,
          borderRadius: 999, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700,
          maxWidth: 240,
        }}
      >
        <Link2 size={13} />
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {linked.title}
        </span>
        <X size={12} style={{ opacity: 0.7 }} />
      </button>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="traction-link-btn"
        style={{
          height: 34, padding: '0 12px',
          background: open ? 'rgba(15,15,30,0.05)' : 'transparent',
          border: '1px solid rgba(15,15,30,0.08)',
          color: 'rgba(15,15,30,0.72)',
          borderRadius: 999,
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600,
          transition: 'all 0.15s',
        }}
      >
        <Link2 size={13} /> Link <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
      </button>
      {open && (
        <LinkDropdown
          onPick={(item) => { setLinked(item); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function LinkDropdown({ onPick, onClose }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState('build');   // 'build' | 'repo'

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/projects/all`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setItems(d.items || []);
        } else setItems([]);
      } catch { setItems([]); }
    })();
  }, []);

  // Close on Esc / outside click
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!items) return [];
    let list = items.filter(i => i.kind !== 'scrape');
    if (tab === 'build') list = list.filter(i => i.kind === 'build');
    if (tab === 'repo') list = list.filter(i => i.kind !== 'build');
    const q = filter.trim().toLowerCase();
    if (q) list = list.filter(i => (i.title || '').toLowerCase().includes(q));
    return list;
  }, [items, filter, tab]);

  return (
    <>
      {/* outside-click capture */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
      <div
        data-testid="traction-link-dropdown"
        style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0,
          width: 380, maxHeight: 380, zIndex: 51,
          background: '#FFFFFF',
          border: '1px solid rgba(15,15,30,0.12)',
          borderRadius: 12,
          boxShadow: '0 20px 50px -10px rgba(15,15,30,0.25)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Tabs */}
        <div style={{ display: 'flex', padding: 8, gap: 6, borderBottom: '1px solid rgba(15,15,30,0.06)' }}>
          <TabBtn active={tab === 'build'} onClick={() => setTab('build')} icon={<Box size={11} />}>Link a build</TabBtn>
          <TabBtn active={tab === 'repo'} onClick={() => setTab('repo')} icon={<Package size={11} />}>Link a repo</TabBtn>
        </div>
        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 12px', borderBottom: '1px solid rgba(15,15,30,0.06)',
        }}>
          <Search size={12} color="rgba(15,15,30,0.4)" />
          <input
            autoFocus
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={tab === 'build' ? 'Search a build…' : 'Search an imported repo…'}
            data-testid="traction-link-search"
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontFamily: 'inherit', fontSize: 12,
              background: 'transparent', color: '#0F0F1A',
            }}
          />
        </div>
        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
          {items === null ? (
            <div style={{ padding: 18, textAlign: 'center', fontSize: 11, color: 'rgba(15,15,30,0.4)' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 18, textAlign: 'center', fontSize: 11, color: 'rgba(15,15,30,0.4)' }}>
              {tab === 'build' ? 'No build found.' : 'No imported repo.'}
            </div>
          ) : filtered.map(it => (
            <button
              key={it.id}
              onClick={() => onPick({ project_id: it.id, title: it.title, kind: it.kind, subtitle: it.subtitle })}
              data-testid={`traction-link-item-${it.id}`}
              style={{
                width: '100%', textAlign: 'left',
                padding: '8px 10px', marginBottom: 2,
                background: 'transparent', border: 'none',
                borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer', fontFamily: 'inherit',
                color: '#0F0F1A',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(15,15,30,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{
                width: 26, height: 26, flexShrink: 0,
                background: it.kind === 'build' ? 'rgba(167,139,250,0.18)' : 'rgba(96,165,250,0.18)',
                color: it.kind === 'build' ? '#8B5CF6' : '#3B82F6',
                borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {it.kind === 'build' ? <Box size={13} /> : <Package size={13} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {it.title}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.45)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {it.subtitle || it.kind}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function TabBtn({ active, onClick, children, icon }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: '6px 10px',
        background: active ? 'rgba(15,15,30,0.06)' : 'transparent',
        border: '1px solid ' + (active ? 'rgba(15,15,30,0.08)' : 'transparent'),
        color: active ? '#0F0F1A' : 'rgba(15,15,30,0.55)',
        borderRadius: 8, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
      }}
    >
      {icon}{children}
    </button>
  );
}

function ExampleCard({ ex, palette, onClick, disabled, delayMs }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`traction-example-${ex.id}`}
      style={{
        textAlign: 'left', padding: '14px 16px',
        background: '#FFFFFF',
        border: '1px solid rgba(15,15,30,0.08)',
        borderRadius: 14,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        boxShadow: '0 4px 12px -4px rgba(15,15,30,0.06)',
        transition: 'all 0.2s ease',
        animation: `fadeIn 0.45s ease-out ${delayMs}ms both`,
        opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 12px 28px -8px ${palette.accent}55, 0 2px 6px rgba(15,15,30,0.06)`;
        e.currentTarget.style.borderColor = `${palette.accent}55`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 4px 12px -4px rgba(15,15,30,0.06)';
        e.currentTarget.style.borderColor = 'rgba(15,15,30,0.08)';
      }}
    >
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: palette.accent === '#5EEAD4' ? '#0EA5A4' : palette.accent,
        marginBottom: 8,
      }}>
        <span style={{
          width: 5, height: 5, borderRadius: 999,
          background: palette.accent, boxShadow: `0 0 6px ${palette.accent}`,
        }} />
        Example traction
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0F0F1A', marginBottom: 4 }}>
        {ex.title}
      </div>
      <div style={{ fontSize: 11.5, color: 'rgba(15,15,30,0.55)', lineHeight: 1.5 }}>
        {ex.subtitle}
      </div>
    </button>
  );
}

const kbdStyle = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10, fontWeight: 700,
  padding: '2px 5px', margin: '0 1px',
  background: 'rgba(15,15,30,0.06)',
  border: '1px solid rgba(15,15,30,0.1)',
  borderRadius: 4,
  color: 'rgba(15,15,30,0.65)',
};
